"use client";

import {
  Bug,
  ChartColumn,
  History,
  PanelRightClose,
  Plus,
  Settings,
} from "lucide-react";
import { Button } from "@/components/shadcn/button";
import { ReleaseStatusBadge } from "@/components/UI/ReleaseStatusBadge";
import { NoxusTooltip } from "@/components/UI/NoxusTooltip";
import { NoxusOnlyIcon } from "@/components/UI/NoxusOnlyIcon";
import { motion, AnimatePresence } from "framer-motion";
import { useCallback, useState } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useRouter } from "next/navigation";
import { useSpotUser } from "@/utils/spotUser";
import { usePermissions } from "@/hooks/usePermissions";
import { ORG_PERMISSIONS } from "@/constants/permissions";
import {
  genieOpenAtom,
  genieDebugLandmarksAtom,
  geniePanelWidthAtom,
  geniePanelExitedAtom,
} from "@/store/genie-atoms";
import { flushEditorSaveSignalAtom } from "@/store/editor-jotai";
import { flushAgentSaveSignalAtom } from "@/store/assistant-editor-jotai";
import { genieTools, genieContext } from "@/lib/genie";
import { useResizablePanel } from "./use-resizable-panel";
import { useGenieConversation } from "./use-genie-conversation";
import { NewAndIncredibleChatWidget } from "@/components/NewAndIncredibleChatWidget/NewAndIncredibleChatWidget";
import { GenieDebugInspector } from "./GenieDebugInspector";
import { GenieChatHistory } from "./GenieChatHistory";
import { GenieChatSkeleton } from "./GenieChatSkeleton";

export function GeniePanel() {
  const [isOpen, setIsOpen] = useAtom(genieOpenAtom);
  const router = useRouter();
  const { spotGroup, spotUser, isLocalNoxus } = useSpotUser();
  const { hasPermission } = usePermissions();
  const canSeeAnalytics = hasPermission(
    ORG_PERMISSIONS.ORG.ANALYTICS,
    "tenant"
  );
  const [panelWidth, setPanelWidth] = useAtom(geniePanelWidthAtom);
  const debugLandmarks = useAtomValue(genieDebugLandmarksAtom);

  const { isResizing, handleResizeStart } = useResizablePanel(
    panelWidth,
    setPanelWidth
  );
  const { conversationId, setConversationId, createConversation, assistantId } =
    useGenieConversation(isOpen);

  const [showInspector, setShowInspector] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const [panelSettled, setPanelSettled] = useState(isOpen);

  const flushEditorSave = useSetAtom(flushEditorSaveSignalAtom);
  const flushAgentSave = useSetAtom(flushAgentSaveSignalAtom);
  const setPanelExited = useSetAtom(geniePanelExitedAtom);

  const transportBody = useCallback(() => {
    flushEditorSave((n) => n + 1);
    flushAgentSave((n) => n + 1);
    return {
      frontend_tools: genieTools.getDefinitions(),
      frontend_context: genieContext.getSnapshot(),
    };
  }, [flushEditorSave, flushAgentSave]);

  return (
    <>
      {debugLandmarks && (
        <style>{`[data-genie-landmark] > * { outline: 2px solid red !important; outline-offset: 2px; }`}</style>
      )}
      {isOpen && (
        // Keeps the Pylon support widget on the main area instead of over the panel
        <style>{`.PylonChat-bubbleFrameContainer { right: ${panelWidth}px !important; bottom: calc(12px + 0.5rem) !important; } .PylonChat-chatWindowFrameContainer { right: calc(${panelWidth}px + 1rem) !important; bottom: calc(12px + 3rem) !important; }`}</style>
      )}
      <AnimatePresence
        initial={false}
        onExitComplete={() => {
          setPanelExited(true);
          setPanelSettled(false);
        }}
      >
        {isOpen && (
          <motion.div
            data-genie-panel
            initial={{ width: 0 }}
            animate={{ width: panelWidth }}
            exit={{ width: 0 }}
            onAnimationStart={() => setPanelExited(false)}
            onAnimationComplete={() => {
              if (isOpen) setPanelSettled(true);
            }}
            transition={
              isResizing
                ? { duration: 0 }
                : { duration: 0.35, ease: [0.4, 0, 0.2, 1] }
            }
            className="shrink-0 h-full overflow-hidden relative"
          >
            <div
              className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize z-10 hover:bg-primary/20 active:bg-primary/30 transition-colors"
              onMouseDown={handleResizeStart}
            />
            <div
              className='flex flex-col h-full pb-[2px] bg-[#F5F6F6] [&_.bg-background]:bg-[#F5F6F6] [&_.group\/input-group]:bg-white [&_[data-slot=input-group]]:rounded-xl [&_a[href*="noxus.ai"]]:hidden'
              style={{ width: panelWidth } as React.CSSProperties}
            >
              <div className="@container flex items-center justify-between px-3 pt-[12px] py-2 shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="New chat"
                  onClick={() => {
                    setShowHistory(false);
                    createConversation(true);
                  }}
                  className="h-8 shrink-0 gap-1.5 text-sm font-medium hover:bg-sidebar-accent"
                >
                  <Plus className="h-4 w-4" />
                  <span className="@max-[300px]:hidden">New chat</span>
                </Button>
                <div className="flex shrink-0 items-center gap-0.5">
                  <NoxusTooltip
                    content="Genie is currently in beta. The experience will keep improving."
                    placement="bottom"
                    asChild
                  >
                    <span className="mr-1 inline-flex">
                      <ReleaseStatusBadge
                        status="beta"
                        className="cursor-default"
                      />
                    </span>
                  </NoxusTooltip>
                  {isLocalNoxus && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowInspector((prev) => !prev)}
                      aria-label="Toggle debug inspector"
                      className="h-8 w-8 hover:bg-sidebar-accent"
                    >
                      <NoxusOnlyIcon size="sm">
                        <Bug className="h-4 w-4" />
                      </NoxusOnlyIcon>
                    </Button>
                  )}
                  {canSeeAnalytics && (
                    <NoxusTooltip
                      content="Genie analytics"
                      placement="bottom"
                      asChild
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          router.push(
                            "/settings/observability/analytics?tab=genie"
                          )
                        }
                        aria-label="Genie analytics"
                        className="h-8 w-8 hover:bg-sidebar-accent"
                      >
                        <ChartColumn className="h-4 w-4" />
                      </Button>
                    </NoxusTooltip>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowHistory((prev) => !prev)}
                    aria-label="Chat history"
                    className="h-8 w-8 hover:bg-sidebar-accent"
                  >
                    <History className="h-4 w-4" />
                  </Button>
                  {spotUser.is_admin && isLocalNoxus && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => router.push("/settings/platform/genie")}
                      aria-label="Genie settings"
                      className="h-8 w-8 hover:bg-sidebar-accent"
                    >
                      <NoxusOnlyIcon size="sm">
                        <Settings className="h-4 w-4" />
                      </NoxusOnlyIcon>
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsOpen(false)}
                    aria-label="Close Genie"
                    className="h-8 w-8 hover:bg-sidebar-accent"
                  >
                    <PanelRightClose className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {showInspector && (
                <GenieDebugInspector onClose={() => setShowInspector(false)} />
              )}
              <div className="flex-1 min-h-0 relative">
                <GenieChatHistory
                  groupId={spotGroup.id}
                  assistantId={assistantId}
                  panelOpen={isOpen}
                  visible={showHistory}
                  onSelect={(id) => {
                    setConversationId(id);
                    setShowHistory(false);
                  }}
                  onClose={() => setShowHistory(false)}
                />
                <motion.div
                  initial={false}
                  animate={{
                    scale: showHistory ? 0.96 : 1,
                    opacity: showHistory ? 0 : 1,
                  }}
                  transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                  className="absolute inset-0"
                  style={
                    {
                      pointerEvents: showHistory ? "none" : "auto",
                      "--accent": "oklch(1 0 0)",
                    } as React.CSSProperties
                  }
                >
                  {conversationId && panelSettled ? (
                    <NewAndIncredibleChatWidget
                      key={conversationId}
                      conversationId={conversationId}
                      groupId={spotGroup.id}
                      assistantId={assistantId}
                      isCompact
                      isGenie
                      transportBody={transportBody}
                      loadingFallback={<GenieChatSkeleton />}
                    />
                  ) : (
                    <GenieChatSkeleton />
                  )}
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
